import { computed, nextTick, ref } from "vue";
import type { ComputedRef, Ref } from "vue";
import type { TextBoxChangeEvent } from "@progress/kendo-vue-inputs";

const DEFAULT_ZIP_ERROR_MESSAGE =
  "Enter a valid ZIP code with exactly 5 or 9 digits.";

const formatZipDisplay = (input: string): string => {
  const digits = input.replace(/\D/g, "").slice(0, 9);
  return digits.length > 5
    ? `${digits.slice(0, 5)}-${digits.slice(5)}`
    : digits;
};

// Translate a formatted caret into the number of ZIP digits before it.
const countDigitsBeforeIndex = (value: string, index: number): number => {
  return value.slice(0, Math.max(0, index)).replace(/\D/g, "").length;
};

// Map a digit index back to the formatted ZIP display position.
const digitIndexToDisplayIndex = (digitIndex: number): number => {
  if (digitIndex <= 5) {
    return digitIndex;
  }

  return Math.min(digitIndex + 1, 10);
};

export type ZipPayload = {
  zip: string;
};

export type UseZipTextBoxOptions = {
  /** The `id` of the Kendo `TextBox` input element. Must match the `id` prop
   * passed to `<TextBox>`. Used by `submitAndValidate({ focus: true })` to focus
   * the field when validation fails. */
  id: string;
  initialValue?: string;
  errorMessage?: string;
  required?: boolean;
};

export type UseZipTextBoxState = {
  zipDisplay: Ref<string>;
  submitted: Ref<boolean>;
  errorMessage: Ref<string>;
};

export type UseZipTextBoxBindings = {
  /** Bind to `:wrapperClass` on `<TextBox>` so Kendo applies `fk-zip-textbox`
   * to its wrapper element on every render — no manual DOM manipulation needed. */
  readonly wrapperClass: string;
};

export type UseZipTextBoxDerived = {
  readonly zipRaw: ComputedRef<string>;
  readonly isZipValid: ComputedRef<boolean>;
  readonly showZipError: ComputedRef<boolean>;
  readonly textBoxValid: ComputedRef<boolean>;
  readonly textBoxValidate: ComputedRef<boolean>;
};

export type UseZipTextBoxActions = {
  handleZipInput: (...args: unknown[]) => void;
  submitAndValidate: (opts?: { focus?: boolean }) => boolean;
  buildPayload: () => ZipPayload;
};

export type UseZipTextBoxApi = {} & UseZipTextBoxState &
  UseZipTextBoxBindings &
  UseZipTextBoxDerived &
  UseZipTextBoxActions;

export const useZipTextBox = (
  options: UseZipTextBoxOptions,
): UseZipTextBoxApi => {
  const zipDisplay = ref(formatZipDisplay(options.initialValue ?? ""));
  const submitted = ref(false);
  const errorMessage = ref(
    options.errorMessage?.trim() || DEFAULT_ZIP_ERROR_MESSAGE,
  );

  const zipRaw = computed(() => zipDisplay.value.replace(/\D/g, ""));
  const isZipValid = computed(() => {
    const length = zipRaw.value.length;
    return length === 5 || length === 9;
  });
  const showZipError = computed(() => submitted.value && !isZipValid.value);
  const textBoxValid = computed(() => !submitted.value || isZipValid.value);
  const textBoxValidate = computed(() => submitted.value);

  // Restore the caret after Vue re-renders the formatted ZIP value.
  const restoreCaret = (
    inputElement: HTMLInputElement,
    digitIndex: number,
  ): void => {
    const caretIndex = digitIndexToDisplayIndex(digitIndex);

    nextTick(() => {
      if (!inputElement.isConnected) {
        return;
      }

      inputElement.setSelectionRange(caretIndex, caretIndex);
    });
  };

  const handleZipInput = (...args: unknown[]) => {
    const event = args[0] as TextBoxChangeEvent | undefined;
    const nativeEvent = event?.event as InputEvent | undefined;
    const inputElement = nativeEvent?.target as HTMLInputElement | null;
    const nextValue = event?.value;
    const inputValue = Array.isArray(nextValue)
      ? nextValue.join("")
      : String(nextValue ?? "");

    // Capture the caret before rewriting the controlled value.
    const digitIndex =
      inputElement?.selectionStart !== undefined &&
      inputElement.selectionStart !== null
        ? countDigitsBeforeIndex(
            inputElement.value,
            inputElement.selectionStart,
          )
        : inputValue.replace(/\D/g, "").length;

    zipDisplay.value = formatZipDisplay(inputValue);

    // Put the caret back where the user was typing.
    if (inputElement) {
      restoreCaret(inputElement, digitIndex);
    }
  };

  const submitAndValidate = (opts?: { focus?: boolean }): boolean => {
    submitted.value = true;
    const valid = isZipValid.value;
    if (!valid && opts?.focus) {
      nextTick(() => {
        const inputEl = document.getElementById(
          options.id,
        ) as HTMLInputElement | null;
        inputEl?.focus();
      });
    }
    return valid;
  };

  const buildPayload = (): ZipPayload => {
    return { zip: zipRaw.value };
  };

  return {
    // writable state
    zipDisplay,
    submitted,
    errorMessage,
    // bindings
    wrapperClass: "fk-zip-textbox",
    // derived state
    zipRaw,
    isZipValid,
    showZipError,
    textBoxValid,
    textBoxValidate,
    // actions
    handleZipInput,
    submitAndValidate,
    buildPayload,
  };
};
